import { useLanguage, type Strings } from "./i18n";

/**
 * Turning the engine's reasons into sentences.
 *
 * The engine returns a code and its arguments — `compacted_soil`, or
 * `heat_spike` with the two temperatures — and never a sentence. It knows *why*
 * and this knows *how to say it*, which is the only way the Arabic interface
 * stops showing English explanations under Arabic plant names.
 *
 * Arguments arrive raw. A Placement is "indoor", not "Indoor" and not
 * "بالداخل", because only this file knows which is wanted.
 */

export interface Reason {
  code: string;
  args?: Record<string, unknown>;
}

const num = (value: unknown): number => (typeof value === "number" ? value : 0);
const str = (value: unknown): string => (typeof value === "string" ? value : "");

/** Enum values the engine passes through, named in the reader's language. */
function placement(value: unknown, t: Strings): string {
  return str(value) === "outdoor" ? t.outdoor : t.indoor;
}

function condition(value: unknown, t: Strings): string {
  switch (str(value)) {
    case "stressed":
      return t.stressed;
    case "recovering":
      return t.recovering;
    default:
      return t.healthy;
  }
}

function product(value: unknown, t: Strings): string {
  return str(value) === "soil_drench" ? t.theSoilFeed : t.theFoliarSpray;
}

/**
 * The sentence for one reason.
 *
 * An unknown code returns null rather than the code itself: a missing
 * translation should be an absence someone notices, not a slug leaking into the
 * interface where it reads as a bug the owner cannot act on.
 */
export function reasonText(reason: Reason, t: Strings): string | null {
  const args = reason.args ?? {};
  switch (reason.code) {
    case "adjusted_by_checks":
      return t.reasonAdjustedByChecks;
    case "own_drying_time":
      return t.reasonOwnDryingTime;
    case "compacted_soil":
      return t.reasonCompactedSoil;
    case "last_watering_unknown":
      return t.reasonLastWateringUnknown;
    case "heat_spike":
      return t.reasonHeatSpike(num(args.forecast_c), num(args.above_normal_c));
    case "own_interval":
      return t.reasonOwnInterval(num(args.days), placement(args.placement, t));
    case "never_sprayed_edible":
      return t.reasonNeverSprayedEdible;
    case "not_while_condition":
      return t.reasonNotWhileCondition(condition(args.condition, t));
    case "recently_repotted":
      return t.reasonRecentlyRepotted;
    case "cold_season":
      return t.reasonColdSeason;
    case "product_separation":
      return t.reasonProductSeparation(num(args.days_ago), num(args.separation_days));
    case "one_at_a_time":
      return t.reasonOneAtATime(product(args.chosen, t), num(args.separation_days));
    case "water_first":
      return t.reasonWaterFirst;
    case "feed_due":
      return t.reasonFeedDue(str(args.dose));
    case "placement_unsuitable":
      return t.reasonPlacementUnsuitable(placement(args.placement, t));
    default:
      return null;
  }
}

/** One reason, rendered. */
export function useReasonText(): (reason: Reason) => string | null {
  const { t } = useLanguage();
  return (reason) => reasonText(reason, t);
}

/** Several reasons, joined, with anything untranslatable dropped. */
export function useReasonList(): (reasons: Reason[] | null | undefined) => string[] {
  const { t } = useLanguage();
  return (reasons) =>
    (reasons ?? [])
      .map((reason) => reasonText(reason, t))
      .filter((text): text is string => text !== null);
}
